export const MICROSOFT_GRAPH_OAUTH_MESSAGE_TYPE = 'microsoft-graph-oauth';

export type MicrosoftGraphOAuthMessage = {
  type: typeof MICROSOFT_GRAPH_OAUTH_MESSAGE_TYPE;
  code: string | null;
  error: string | null;
};

export type OAuthPopupResult = {
  code: string | null;
  error: string | null;
};

export function isMicrosoftGraphOAuthMessage(data: unknown): data is MicrosoftGraphOAuthMessage {
  if (!data || typeof data !== 'object') {
    return false;
  }
  const message = data as MicrosoftGraphOAuthMessage;
  return message.type === MICROSOFT_GRAPH_OAUTH_MESSAGE_TYPE;
}

export function parseOAuthResultFromUrl(url: string): OAuthPopupResult {
  try {
    const parsed = new URL(url);
    const code = parsed.searchParams.get('code');
    const error = parsed.searchParams.get('error');
    const errorDescription = parsed.searchParams.get('error_description');
    return {
      code,
      error: error ? (errorDescription ? `${error}: ${errorDescription}` : error) : null,
    };
  } catch {
    return { code: null, error: null };
  }
}

export function extractOAuthCodeFromUrl(url: string): string | null {
  return parseOAuthResultFromUrl(url).code;
}

const POPUP_WIDTH = 520;
const POPUP_HEIGHT = 720;

export function openMicrosoftGraphOAuthPopup(authUrl: string): Window | null {
  const left = Math.max(0, window.screenX + (window.outerWidth - POPUP_WIDTH) / 2);
  const top = Math.max(0, window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2);
  const features = [
    `width=${POPUP_WIDTH}`,
    `height=${POPUP_HEIGHT}`,
    `left=${left}`,
    `top=${top}`,
    'scrollbars=yes',
    'resizable=yes',
  ].join(',');

  return window.open(authUrl, 'microsoft-graph-oauth', features);
}

/**
 * Poll the popup URL once Microsoft redirects back to our origin.
 * Works even when the callback page is blank (e.g. /api proxied incorrectly).
 */
export function watchOAuthPopupForCode(
  popup: Window,
  callbacks: {
    onResult: (result: OAuthPopupResult) => void;
    onClosed: () => void;
  },
): () => void {
  let finished = false;

  const finish = (action: () => void) => {
    if (finished) {
      return;
    }
    finished = true;
    window.clearInterval(intervalId);
    action();
  };

  const intervalId = window.setInterval(() => {
    if (popup.closed) {
      finish(callbacks.onClosed);
      return;
    }

    try {
      const href = popup.location.href;
      if (!href || href === 'about:blank') {
        return;
      }

      const result = parseOAuthResultFromUrl(href);
      if (result.code || result.error) {
        try {
          popup.close();
        } catch {
          // Popup may already be closing
        }
        finish(() => callbacks.onResult(result));
      }
    } catch {
      // Cross-origin while on login.microsoftonline.com — keep polling
    }
  }, 350);

  return () => {
    finished = true;
    window.clearInterval(intervalId);
  };
}
